import { Button, Card } from "@material-ui/core";
import CardContent from "@material-ui/core/CardContent";
import TextField from "@material-ui/core/TextField";
import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import ControlledCheckbox from "@saleor/components/ControlledCheckbox";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { Tab, TabContainer } from "@saleor/components/Tab";
import { commonMessages, sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { Redemption_redemption } from "@saleor/specialOffer/types/Redemption";
import { ListProps2, ReorderAction, TabListActions } from "@saleor/types";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import SpecialOfferVariants from "../SpecialOfferVariants";
export interface SpecialOfferDetailsProps
  extends Pick<ListProps2, Exclude<keyof ListProps2, "onRowClick">>,
    TabListActions<"variantListToolbar"> {
  specialOffer: Redemption_redemption;
  onSubmit: (data: SpecialOfferFormData) => void;
  onBack: () => void;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  activeTab: SpecialOfferDetailsPageTab;
  defaultCreate: () => void;
  onTabClick: (index: SpecialOfferDetailsPageTab) => void;
  onVariantAssign: () => void;
  onVariantUnassign: (id: string) => void;
  onVariantClick: (productId: string, variantId: string) => () => void;
  onVariantReorder: ReorderAction;
}

export interface VariantsData {
  variantId: string;
  discountPrice: string;
  sortOrder: any;
}

export interface SpecialOfferFormData {
  name: string;
  description: string;
  minOrderAmount: number;
  enable: boolean;
  redemptionVariants?: VariantsData[];
  maxRedemptionAmount: number;
  signText: string;
}

export enum SpecialOfferDetailsPageTab {
  variants = "variants"
}

export function saleDetailsPageTab(tab: string): SpecialOfferDetailsPageTab {
  return tab === SpecialOfferDetailsPageTab.variants
    ? SpecialOfferDetailsPageTab.variants
    : SpecialOfferDetailsPageTab.variants;
}

const VariantsTab = Tab(SpecialOfferDetailsPageTab.variants);

const SpecialOfferDetails: React.FC<SpecialOfferDetailsProps> = ({
  specialOffer,
  onSubmit,
  disabled,
  onBack,
  activeTab,
  onTabClick,
  defaultCreate,
  saveButtonBarState,
  // onNextPage,
  // onPreviousPage,
  onVariantAssign,
  onVariantUnassign,
  onVariantReorder,
  onVariantClick,
  isChecked,
  selected,
  toggle,
  toggleAll,
  variantListToolbar,
  pageInfo
}) => {
  const initialForm: SpecialOfferFormData = {
    name: maybe(() => specialOffer.name, ""),
    description: maybe(() => specialOffer.description, ""),
    minOrderAmount: maybe(() => specialOffer.minOrderAmount, 0),
    enable: maybe(() => specialOffer.enable, false),
    maxRedemptionAmount: maybe(() => specialOffer.maxRedemptionAmount, 0),
    signText: maybe(() => specialOffer.signText, ""),
    redemptionVariants: maybe(
      () =>
        specialOffer.redemptionVariants.map(variant => ({
          variantId: variant.variant.id,
          discountPrice: variant.discountPrice.toString(),
          sortOrder: variant.sortOrder || null
        })),
      null
    )
  };
  const intl = useIntl();
  return (
    <>
      <Form initial={initialForm} onSubmit={onSubmit}>
        {({ change, data, hasChanged, submit }) => (
          <Container>
            <AppHeader onBack={onBack}>
              <FormattedMessage {...sectionNames.configuration} />
            </AppHeader>
            <PageHeader title={"Special Offer"} />
            <Grid>
              {specialOffer ? (
                <>
                  <Card>
                    <CardTitle
                      title={intl.formatMessage(
                        commonMessages.generalInformations
                      )}
                    />
                    <CardContent>
                      <TextField
                        disabled={disabled}
                        label={intl.formatMessage({
                          defaultMessage: "Name",
                          description: "Name"
                        })}
                        name={"name" as keyof SpecialOfferFormData}
                        fullWidth
                        value={data.name}
                        onChange={change}
                      />
                      <FormSpacer />
                      <TextField
                        disabled={disabled}
                        label={intl.formatMessage({
                          defaultMessage: "Description",
                          description: "description"
                        })}
                        name={"description" as keyof SpecialOfferFormData}
                        fullWidth
                        value={data.description}
                        onChange={change}
                        helperText={`If currency is included, please use the specified format, e.g. US $\{10\}`}
                      />
                      <FormSpacer />
                      <TextField
                        disabled={disabled}
                        label={intl.formatMessage({
                          defaultMessage: "Min Order Amount",
                          description: "minOrderAmount"
                        })}
                        name={"minOrderAmount" as keyof SpecialOfferFormData}
                        fullWidth
                        value={data.minOrderAmount}
                        onChange={change}
                      />
                      <FormSpacer />
                      <TextField
                        disabled={disabled}
                        label={intl.formatMessage({
                          defaultMessage: "Max Redemption Amount",
                          description: "maxRedemptionAmount"
                        })}
                        name={
                          "maxRedemptionAmount" as keyof SpecialOfferFormData
                        }
                        fullWidth
                        value={data.maxRedemptionAmount}
                        onChange={change}
                      />
                      <FormSpacer />
                      <TextField
                        disabled={disabled}
                        label={intl.formatMessage({
                          defaultMessage: "Sign Text",
                          description: "signText"
                        })}
                        name={"signText" as keyof SpecialOfferFormData}
                        fullWidth
                        value={data.signText}
                        onChange={change}
                      />
                      <FormSpacer />
                      <ControlledCheckbox
                        name={"enable" as keyof SpecialOfferFormData}
                        label={intl.formatMessage({
                          defaultMessage: "Enable",
                          description: "enable"
                        })}
                        checked={data.enable}
                        onChange={change}
                        disabled={disabled}
                      />
                    </CardContent>
                  </Card>
                  <CardSpacer />
                  <TabContainer>
                    <VariantsTab
                      isActive={
                        activeTab === SpecialOfferDetailsPageTab.variants
                      }
                      changeTab={onTabClick}
                    >
                      {/* {intl.formatMessage(
                                        {
                                            defaultMessage: "SKU({quantity})",
                                            description: "number of skus",
                                            id: "saleDetailsPageVariantsQuantity"
                                        },
                                        {
                                            quantity: maybe(
                                            () => specialOffer.redemptionVariants.totalCount.toString(),
                                            "…"
                                            )
                                        }
                                        )} */}
                      {intl.formatMessage({
                        defaultMessage: "SKU",
                        description: "number of skus",
                        id: "saleDetailsPageVariantsQuantity"
                      })}
                    </VariantsTab>
                  </TabContainer>
                  <CardSpacer />
                  {activeTab === SpecialOfferDetailsPageTab.variants ? (
                    <SpecialOfferVariants
                      onVariantReorder={onVariantReorder}
                      disabled={disabled}
                      onNextPage={null}
                      onPreviousPage={null}
                      onVariantAssign={onVariantAssign}
                      onVariantUnassign={onVariantUnassign}
                      onRowClick={onVariantClick}
                      pageInfo={pageInfo}
                      specialOffer={specialOffer}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={variantListToolbar}
                      onChange={change}
                      data={data}
                    />
                  ) : (
                    <SpecialOfferVariants
                      onVariantReorder={onVariantReorder}
                      disabled={disabled}
                      onNextPage={null}
                      onPreviousPage={null}
                      onVariantAssign={onVariantAssign}
                      onVariantUnassign={onVariantUnassign}
                      onRowClick={onVariantClick}
                      pageInfo={pageInfo}
                      specialOffer={specialOffer}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={variantListToolbar}
                      onChange={change}
                      data={data}
                    />
                  )}
                </>
              ) : (
                <Card>
                  <CardContent>
                    <Button
                      color="primary"
                      onClick={defaultCreate}
                      disabled={disabled}
                    >
                      <FormattedMessage
                        defaultMessage="Create Default"
                        description="button"
                      />
                    </Button>

                    <CardSpacer />
                    <p style={{ color: "#999", marginLeft: "20px" }}>
                      {
                        "New special-offer window has not been set, please initialize first"
                      }
                    </p>
                  </CardContent>
                </Card>
              )}
            </Grid>
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              state={saveButtonBarState}
              onCancel={onBack}
              onSave={submit}
              onDelete={undefined}
            />
          </Container>
        )}
      </Form>
    </>
  );
};
export default SpecialOfferDetails;
